/**
 * Lightweight GeoJSON geometry helpers (no turf dependency).
 * Used to recenter MapLibre maps on the active territory's real extent.
 */

/** [west, south, east, north] in WGS84 degrees. */
export type BBox = [number, number, number, number];

/** Recursively walk every [lon, lat] position inside a coordinate array. */
function walkPositions(
  coords: unknown,
  visit: (lon: number, lat: number) => void,
): void {
  if (!Array.isArray(coords)) return;
  // A position is [number, number, ...].
  if (typeof coords[0] === 'number' && typeof coords[1] === 'number') {
    visit(coords[0], coords[1]);
    return;
  }
  for (const child of coords) {
    walkPositions(child, visit);
  }
}

/**
 * Compute the bounding box of a GeoJSON geometry.
 * Returns null for empty/unsupported geometries (e.g. GeometryCollection without coords).
 */
export function geometryBounds(geometry: GeoJSON.Geometry | null | undefined): BBox | null {
  if (!geometry) return null;

  let minLon = Infinity;
  let minLat = Infinity;
  let maxLon = -Infinity;
  let maxLat = -Infinity;

  const visit = (lon: number, lat: number): void => {
    if (lon < minLon) minLon = lon;
    if (lat < minLat) minLat = lat;
    if (lon > maxLon) maxLon = lon;
    if (lat > maxLat) maxLat = lat;
  };

  if (geometry.type === 'GeometryCollection') {
    for (const g of geometry.geometries) {
      walkPositions((g as { coordinates?: unknown }).coordinates, visit);
    }
  } else {
    walkPositions(geometry.coordinates, visit);
  }

  if (
    !Number.isFinite(minLon) ||
    !Number.isFinite(minLat) ||
    !Number.isFinite(maxLon) ||
    !Number.isFinite(maxLat)
  ) {
    return null;
  }

  return [minLon, minLat, maxLon, maxLat];
}

/** Geometric centre of a bbox as [lon, lat]. */
export function bboxCenter(bbox: BBox): [number, number] {
  return [(bbox[0] + bbox[2]) / 2, (bbox[1] + bbox[3]) / 2];
}
